/**
 * Geometry primitives.
 *
 * EVERYTHING that leaves the CV layer towards the rule engine or the UI is
 * expressed in *normalized image coordinates*: `x` and `y` in `[0, 1]`,
 * origin at the top-left corner, `y` growing downwards.
 *
 * This is the single most important convention of the project: one validator
 * run then works unchanged for a 600x600 upload, a 1200x1200 upload, a mobile
 * preview, a desktop preview and the print-free diagnostic overlay.
 */

export interface NormalizedPoint {
  /** Horizontal position, 0 = left edge, 1 = right edge. */
  x: number;
  /** Vertical position, 0 = top edge, 1 = bottom edge. */
  y: number;
}

/** Rectangle in normalized coordinates. */
export interface NormalizedRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Rectangle in pixels of the original image. */
export interface PixelRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * A drawable, normalized shape produced by the CV layer and consumed verbatim
 * by the overlay renderer. The overlay never computes geometry itself.
 */
export type NormalizedGeometry =
  | { type: 'point'; coordinates: [number, number] }
  | { type: 'line'; coordinates: [number, number, number, number] }
  | { type: 'rect'; coordinates: [number, number, number, number] }
  /** center x, center y, radius x, radius y */
  | { type: 'ellipse'; coordinates: [number, number, number, number] }
  | { type: 'polygon'; coordinates: number[] };

export interface NormalizedLandmark {
  x: number;
  y: number;
  z?: number;
}

export const clamp = (value: number, min: number, max: number): number =>
  value < min ? min : value > max ? max : value;

export const clamp01 = (value: number): number => clamp(value, 0, 1);

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

export const roundTo = (value: number, digits = 3): number => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

export const distance = (a: NormalizedPoint, b: NormalizedPoint): number =>
  Math.hypot(a.x - b.x, a.y - b.y);

export const midpoint = (a: NormalizedPoint, b: NormalizedPoint): NormalizedPoint => ({
  x: (a.x + b.x) / 2,
  y: (a.y + b.y) / 2,
});

export const degrees = (radians: number): number => (radians * 180) / Math.PI;

export const rectFromPoints = (points: NormalizedPoint[]): NormalizedRect => {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    if (point.x < minX) minX = point.x;
    if (point.y < minY) minY = point.y;
    if (point.x > maxX) maxX = point.x;
    if (point.y > maxY) maxY = point.y;
  }
  if (!Number.isFinite(minX)) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

export const rectCenter = (rect: NormalizedRect): NormalizedPoint => ({
  x: rect.x + rect.width / 2,
  y: rect.y + rect.height / 2,
});

export const rectContains = (rect: NormalizedRect, point: NormalizedPoint): boolean =>
  point.x >= rect.x &&
  point.x <= rect.x + rect.width &&
  point.y >= rect.y &&
  point.y <= rect.y + rect.height;

export const expandRect = (rect: NormalizedRect, by: number): NormalizedRect => ({
  x: rect.x - by,
  y: rect.y - by,
  width: rect.width + by * 2,
  height: rect.height + by * 2,
});

export const intersects = (a: NormalizedRect, b: NormalizedRect): boolean =>
  a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;

/** Convert a normalized rect to pixels, clamped to the image bounds. */
export const toPixelRect = (rect: NormalizedRect, width: number, height: number): PixelRect => {
  const x = clamp(Math.round(rect.x * width), 0, width);
  const y = clamp(Math.round(rect.y * height), 0, height);
  const w = clamp(Math.round(rect.width * width), 0, width - x);
  const h = clamp(Math.round(rect.height * height), 0, height - y);
  return { x, y, width: w, height: h };
};

/** Format a normalized value as the percentage string the UI shows. */
export const toPercent = (value: number, digits = 1): number => roundTo(value * 100, digits);

/**
 * Clamps a shape into the 0..1 image box.
 *
 * Guides and measured shapes are drawn *over* the photo, so anything sticking
 * out of the frame is meaningless to the renderer (and to the user). The core
 * guarantees the invariant here, once, instead of in every renderer.
 */
export const clampNormalizedGeometry = (shape: NormalizedGeometry): NormalizedGeometry => {
  const c = (value: number): number => clamp(value, 0, 1);
  const coordinates = shape.coordinates.map(c);
  switch (shape.type) {
    case 'rect': {
      const [x, y, width, height] = coordinates as [number, number, number, number];
      const clampedX = c(x);
      const clampedY = c(y);
      return {
        type: 'rect',
        coordinates: [
          clampedX,
          clampedY,
          c(clampedX + width) - clampedX,
          c(clampedY + height) - clampedY,
        ],
      };
    }
    case 'line':
      return { type: 'line', coordinates: coordinates as [number, number, number, number] };
    case 'point':
      return { type: 'point', coordinates: coordinates as [number, number] };
    case 'ellipse':
      return { type: 'ellipse', coordinates: coordinates as [number, number, number, number] };
    case 'polygon':
      return { type: 'polygon', coordinates };
    default:
      return shape;
  }
};
